import React, { useState } from 'react';
import { Calendar, Clock, MapPin, Send, Loader2, X, Plus, Minus, Upload, Image as ImageIcon } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Calendar as CalendarComponent } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { format } from 'date-fns';
import { ro, enUS } from 'date-fns/locale';
import { toast } from 'sonner';

const BookingModal = ({ isOpen, onClose, t, currentLang }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [date, setDate] = useState();
  const [uploadedPhotos, setUploadedPhotos] = useState([]);
  const [gdprConsent, setGdprConsent] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    itemCount: 1,
    preferredTime: '',
    address: '',
    details: ''
  });

  const timeSlots = [
    '08:00 - 10:00',
    '10:00 - 12:00',
    '12:00 - 14:00',
    '14:00 - 16:00',
    '16:00 - 18:00',
    '18:00 - 20:00'
  ];

  const getAvailableTimeSlots = (selectedDate) => {
    if (!selectedDate) return timeSlots;
    
    const dayOfWeek = selectedDate.getDay(); // 0 = Sunday, 6 = Saturday
    
    // Sâmbătă (6) - doar 08:00-14:00
    if (dayOfWeek === 6) {
      return [
        '08:00 - 10:00',
        '10:00 - 12:00',
        '12:00 - 14:00'
      ];
    }
    
    // Duminică (0) - închis
    if (dayOfWeek === 0) {
      return [];
    }
    
    // Luni-Vineri - toate intervalele
    return timeSlots;
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    const maxFiles = 5;
    const maxSize = 5 * 1024 * 1024; // 5MB per file
    
    // Filter valid files
    const validFiles = files.filter(file => {
      if (file.size > maxSize) {
        toast.error(currentLang === 'ro' ? 'Fișierul este prea mare (max 5MB)' : 'File too large (max 5MB)');
        return false;
      }
      if (!file.type.startsWith('image/')) {
        toast.error(currentLang === 'ro' ? 'Doar imagini sunt permise' : 'Only images allowed');
        return false;
      }
      return true;
    });
    
    const newPhotos = validFiles.slice(0, maxFiles - uploadedPhotos.length);
    
    // Create preview URLs
    const photoPromises = newPhotos.map(file => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          resolve({
            file,
            preview: reader.result,
            name: file.name
          });
        };
        reader.readAsDataURL(file);
      });
    });
    
    Promise.all(photoPromises).then(photos => {
      setUploadedPhotos(prev => [...prev, ...photos]);
    });
  };

  const removePhoto = (index) => {
    setUploadedPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!date) {
      toast.error(currentLang === 'ro' ? 'Te rugăm să selectezi o dată' : 'Please select a date');
      return;
    }

    if (!gdprConsent) {
      toast.error(currentLang === 'ro' ? 'Te rugăm să accepți termenii de prelucrare a datelor' : 'Please accept data processing terms');
      return;
    }

    setIsSubmitting(true);

    const bookingData = {
      ...formData,
      preferredDate: date.toISOString(),
      language: currentLang,
      photos: uploadedPhotos.map(p => p.preview) // Base64 encoded photos
    };

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/bookings/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bookingData),
      });

      if (!response.ok) {
        throw new Error('Failed to submit booking');
      }

      const result = await response.json();
      console.log('Booking submitted:', result);
      
      // Google Ads Conversion Tracking
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'conversion', {
          'send_to': 'AW-18007546219/zD5fCPPB4oscEOuy1YpD',
          'value': 1.0,
          'currency': 'RON'
        });
      }
      
      toast.success(
        currentLang === 'ro' 
          ? '✅ Solicitare trimisă! Te vom contacta în curând pentru confirmare.' 
          : '✅ Request sent! We will contact you soon for confirmation.'
      );
      
      setFormData({
        name: '',
        email: '',
        phone: '',
        service: '',
        itemCount: 1,
        preferredTime: '',
        address: '',
        details: ''
      });
      setDate(undefined);
      setUploadedPhotos([]);
      setGdprConsent(false);
      onClose();
    } catch (error) {
      console.error('Booking error:', error);
      toast.error(
        currentLang === 'ro'
          ? '❌ A apărut o eroare. Te rugăm să încerci din nou.'
          : '❌ An error occurred. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const translations = {
    ro: {
      title: 'Solicită Programare',
      description: 'Completează formularul și te vom contacta pentru confirmare și detalii despre preț',
      serviceLabel: 'Tipul serviciului',
      selectService: 'Selectează serviciul',
      couchCleaning: 'Curățare Canapele',
      mattressCleaning: 'Curățare Saltele',
      both: 'Ambele',
      itemCountLabel: 'Număr de piese',
      itemCountHelper: 'Câte canapele/saltele dorești să cureți?',
      dateLabel: 'Data preferată',
      selectDate: 'Alege data',
      timeLabel: 'Ora preferată',
      selectTime: 'Alege intervalul orar',
      addressLabel: 'Adresa în Brașov',
      addressPlaceholder: 'Strada, număr, bloc, etc.',
      detailsLabel: 'Detalii suplimentare',
      detailsPlaceholder: 'Dimensiuni aproximative, culoare, stare, pete specifice...',
      photosLabel: 'Fotografii (opțional)',
      photosHelper: 'Adaugă fotografii cu canapelele/saltelele (max 5 fotografii, 5MB fiecare)',
      uploadButton: 'Adaugă Fotografii',
      gdprLabel: 'Acord prelucrare date personale',
      gdprText: 'Sunt de acord ca datele mele personale să fie prelucrate conform ',
      gdprLink: 'Politicii de confidențialitate GDPR',
      gdprError: 'Trebuie să accepți termenii',
      submitButton: 'Trimite Solicitarea',
      submitting: 'Se trimite...',
      cancel: 'Anulează'
    },
    en: {
      title: 'Request Appointment',
      description: 'Fill out the form and we will contact you for confirmation and price details',
      serviceLabel: 'Service type',
      selectService: 'Select service',
      couchCleaning: 'Couch Cleaning',
      mattressCleaning: 'Mattress Cleaning',
      both: 'Both',
      itemCountLabel: 'Number of items',
      itemCountHelper: 'How many couches/mattresses do you want to clean?',
      dateLabel: 'Preferred date',
      selectDate: 'Choose date',
      timeLabel: 'Preferred time',
      selectTime: 'Choose time slot',
      addressLabel: 'Address in Brașov',
      addressPlaceholder: 'Street, number, building, etc.',
      detailsLabel: 'Additional details',
      detailsPlaceholder: 'Approximate dimensions, color, condition, specific stains...',
      photosLabel: 'Photos (optional)',
      photosHelper: 'Add photos of couches/mattresses (max 5 photos, 5MB each)',
      uploadButton: 'Add Photos',
      gdprLabel: 'Personal data processing agreement',
      gdprText: 'I agree that my personal data will be processed according to the ',
      gdprLink: 'GDPR Privacy Policy',
      gdprError: 'You must accept the terms',
      submitButton: 'Send Request',
      submitting: 'Sending...',
      cancel: 'Cancel'
    }
  };

  const txt = translations[currentLang];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-[#0d2e5d]">
            {txt.title}
          </DialogTitle>
          <DialogDescription className="text-gray-600">
            {txt.description}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          {/* Contact Info */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="name">{t.contact.form.name} *</Label>
              <Input
                id="name"
                required
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="Ion Popescu"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="phone">{t.contact.form.phone} *</Label>
              <Input
                id="phone"
                required
                type="tel"
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                placeholder="+40 7XX XXX XXX"
                className="mt-1"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="email">{t.contact.form.email} *</Label>
            <Input
              id="email"
              required
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              placeholder="ion@example.com"
              className="mt-1"
            />
          </div>

          {/* Service Selection */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="service">{txt.serviceLabel} *</Label>
              <Select
                required
                value={formData.service}
                onValueChange={(value) => handleChange('service', value)}
              >
                <SelectTrigger className="mt-1 relative z-50">
                  <SelectValue placeholder={txt.selectService} />
                </SelectTrigger>
                <SelectContent className="z-[100]">
                  <SelectItem value="couch">{txt.couchCleaning}</SelectItem>
                  <SelectItem value="mattress">{txt.mattressCleaning}</SelectItem>
                  <SelectItem value="both">{txt.both}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="itemCount">{txt.itemCountLabel} *</Label>
              <div className="flex items-center gap-2 mt-1">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => handleChange('itemCount', Math.max(1, formData.itemCount - 1))}
                  disabled={formData.itemCount <= 1}
                >
                  <Minus className="w-4 h-4" />
                </Button>
                <Input
                  id="itemCount"
                  type="number"
                  min="1"
                  max="20"
                  value={formData.itemCount}
                  onChange={(e) => handleChange('itemCount', parseInt(e.target.value) || 1)}
                  className="text-center"
                  required
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => handleChange('itemCount', Math.min(20, formData.itemCount + 1))}
                  disabled={formData.itemCount >= 20}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-gray-500 mt-1">{txt.itemCountHelper}</p>
            </div>
          </div>

          {/* Date & Time */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>{txt.dateLabel} *</Label>
              <Popover modal={true}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal mt-1 relative z-50"
                  >
                    <Calendar className="mr-2 h-4 w-4" />
                    {date ? format(date, 'PPP', { locale: currentLang === 'ro' ? ro : enUS }) : txt.selectDate}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 z-[100]" align="start">
                  <CalendarComponent
                    mode="single"
                    selected={date}
                    onSelect={(newDate) => {
                      setDate(newDate);
                      // Reset time if Sunday selected
                      if (newDate && newDate.getDay() === 0) {
                        setFormData(prev => ({ ...prev, preferredTime: '' }));
                      }
                    }}
                    disabled={(date) => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      // Disable past dates and Sundays
                      return date < today || date.getDay() === 0;
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div>
              <Label htmlFor="time">{txt.timeLabel} *</Label>
              <Select
                required
                value={formData.preferredTime}
                onValueChange={(value) => handleChange('preferredTime', value)}
              >
                <SelectTrigger className="mt-1 relative z-50">
                  <SelectValue placeholder={txt.selectTime} />
                </SelectTrigger>
                <SelectContent className="z-[100]">
                  {getAvailableTimeSlots(date).length === 0 ? (
                    <div className="p-4 text-center text-gray-500 text-sm">
                      {currentLang === 'ro' ? 'Închis Duminică' : 'Closed on Sunday'}
                    </div>
                  ) : (
                    getAvailableTimeSlots(date).map((slot) => (
                      <SelectItem key={slot} value={slot}>
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          {slot}
                        </div>
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              {date && date.getDay() === 6 && (
                <p className="text-xs text-amber-600 mt-1">
                  {currentLang === 'ro' ? '⚠️ Sâmbătă: Program redus 08:00-14:00' : '⚠️ Saturday: Reduced hours 08:00-14:00'}
                </p>
              )}
            </div>
          </div>

          {/* Address */}
          <div>
            <Label htmlFor="address">
              <MapPin className="w-4 h-4 inline mr-1" />
              {txt.addressLabel} *
            </Label>
            <Input
              id="address"
              required
              value={formData.address}
              onChange={(e) => handleChange('address', e.target.value)}
              placeholder={txt.addressPlaceholder}
              className="mt-1"
            />
          </div>

          {/* Additional Details */}
          <div>
            <Label htmlFor="details">{txt.detailsLabel}</Label>
            <Textarea
              id="details"
              value={formData.details}
              onChange={(e) => handleChange('details', e.target.value)}
              placeholder={txt.detailsPlaceholder}
              rows={4}
              className="resize-none mt-1"
            />
          </div>

          {/* Photo Upload */}
          <div>
            <Label htmlFor="photos">
              <ImageIcon className="w-4 h-4 inline mr-1" />
              {txt.photosLabel}
            </Label>
            <p className="text-xs text-gray-500 mb-2">{txt.photosHelper}</p>
            
            <div className="space-y-3">
              {/* Upload Button */}
              {uploadedPhotos.length < 5 && (
                <label className="cursor-pointer">
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-[#2196f3] transition-colors">
                    <div className="flex items-center justify-center gap-2 text-gray-600 hover:text-[#2196f3]">
                      <Upload className="w-5 h-5" />
                      <span className="font-medium">{txt.uploadButton}</span>
                    </div>
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handlePhotoUpload}
                    className="hidden"
                  />
                </label>
              )}
              
              {/* Photo Previews */}
              {uploadedPhotos.length > 0 && (
                <div className="grid grid-cols-3 gap-3">
                  {uploadedPhotos.map((photo, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={photo.preview}
                        alt={`Preview ${index + 1}`}
                        className="w-full h-24 object-cover rounded-lg border-2 border-gray-200"
                      />
                      <button
                        type="button"
                        onClick={() => removePhoto(index)}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              {uploadedPhotos.length > 0 && (
                <p className="text-xs text-gray-500">
                  {uploadedPhotos.length} / 5 {currentLang === 'ro' ? 'fotografii încărcate' : 'photos uploaded'}
                </p>
              )}
            </div>
          </div>

          {/* GDPR Consent */}
          <div className="border-t pt-6">
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="gdpr-consent"
                checked={gdprConsent}
                onChange={(e) => setGdprConsent(e.target.checked)}
                className="mt-1 w-4 h-4 text-[#2196f3] border-gray-300 rounded focus:ring-[#2196f3]"
                required
              />
              <label htmlFor="gdpr-consent" className="text-sm text-gray-700 leading-relaxed">
                {txt.gdprText}
                <span className="text-[#2196f3] underline cursor-pointer hover:text-[#1976d2]">
                  {txt.gdprLink}
                </span>
                . {currentLang === 'ro' 
                  ? 'Datele tale vor fi folosite doar pentru procesarea solicitării și nu vor fi partajate cu terțe părți.'
                  : 'Your data will only be used for processing your request and will not be shared with third parties.'}
              </label>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
              disabled={isSubmitting}
            >
              {txt.cancel}
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 bg-[#2196f3] hover:bg-[#1976d2] text-white"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {txt.submitting}
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  {txt.submitButton}
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default BookingModal;
